// One-line exposure logging for advisor-shaped UI (DSS P0,
// docs/specs/decision-support-system.md §5.1). Call where an advisor verdict
// is actually on screen; the module-level throttle in decisionEvents keeps it
// to one 'shown' per advisor+entity per tab session, so callers re-render
// freely without spamming the table.

import { useEffect, useRef } from 'react'
import { logDecisionEvent, type AdvisorRecommendation } from '@/lib/decisionEvents'

export interface AdvisorShownInput {
  advisorKey:     string
  entityType?:    string
  entityId?:      string | null
  recommendation: AdvisorRecommendation
  /** Pass false while inputs are still loading so a transient 'unknown' is
   *  not recorded as the exposure. Default true. */
  enabled?: boolean
}

export function useAdvisorShown({ advisorKey, entityType, entityId, recommendation, enabled = true }: AdvisorShownInput): void {
  // Latest-ref: reasons/evidence get fresh identities every render — the
  // exposure is keyed on advisor+entity+verdict, not on prose identity.
  const recRef = useRef(recommendation)
  recRef.current = recommendation
  const { verdict } = recommendation

  useEffect(() => {
    if (!enabled || !entityId) return
    logDecisionEvent({
      advisorKey,
      eventType: 'shown',
      entityType,
      entityId,
      recommendation: recRef.current,
    })
  }, [advisorKey, entityType, entityId, verdict, enabled])
}
